/**
 * F-507-FE-01: JoinPage
 *
 * Patient entry screen at /join — two join methods:
 * 1. QR code scan via device camera (jsQR + canvas frame analysis)
 * 2. Manual 6-digit code entry (ATM-style large numeric input)
 *
 * Design: Warm Wayfinding — calm, large, no chrome. Arabic RTL at pilot launch.
 * Touch targets: 56px minimum. Font: 20px+ base.
 *
 * Flow:
 * - Patient arrives via URL: /join?sessionId=xxx&token=yyy (auto-joins)
 * - Or scans QR code encoding that URL (extracts sessionId + token)
 * - Or enters 6-digit code manually (uses sessionId from URL params)
 * - POST /sessions/join  { sessionId, token } or { sessionId, code }
 * - On success: navigate to /session/:id/disclaimer
 * - On error: display localised error message
 *
 * This is an unauthenticated flow — no JWT required.
 */

import {
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import jsQR from 'jsqr';
import { ApiError } from '@/lib/api';

// ---------------------------------------------------------------------------
// Localised strings (Arabic pilot — extend to i18n system later)
// ---------------------------------------------------------------------------

const STRINGS = {
  title: 'انضم إلى الجلسة',                               // Join Session
  subtitle: 'امسح رمز QR أو أدخل الرمز المكوّن من ٦ أرقام',// Scan QR or enter 6-digit code
  orDivider: 'أو',                                         // or
  scanPrompt: 'وجّه الكاميرا نحو رمز QR',                  // Point camera at QR code
  manualLabel: 'أدخل الرمز يدوياً',                        // Enter code manually
  manualPlaceholder: '000000',
  joinButton: 'انضمام',                                    // Join
  scanButton: 'امسح رمز QR',                               // Scan QR
  cancelScan: 'إلغاء المسح',                               // Cancel scan
  errorExpired: 'انتهت صلاحية هذا الرمز. اطلب من طبيبك رمزاً جديداً.',
  errorInvalid: 'رمز غير صحيح. يرجى المحاولة مرة أخرى.',
  errorCamera: 'لا يمكن الوصول إلى الكاميرا. يرجى إدخال الرمز يدوياً.',
  errorGeneric: 'حدث خطأ. يرجى المحاولة مرة أخرى.',
  scanning: 'جارٍ المسح...',                                // Scanning...
  joining: 'جارٍ الانضمام...',                              // Joining...
};

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface JoinResponse {
  sessionId: string;
  patientLanguageCode: string;
  doctorLanguageCode: string;
  /** Short-lived JWT for the patient to authenticate their WebSocket connection. */
  wsToken: string;
}

/** SessionStorage key for the patient session token (consumed by PatientSessionPage) */
const SESSION_TOKEN_KEY = 'patient_session_token';

// ---------------------------------------------------------------------------
// API call (unauthenticated)
// ---------------------------------------------------------------------------

const BASE_URL = (import.meta.env.VITE_API_URL as string | undefined) ?? '';

async function joinSession(payload: { sessionId?: string; token?: string; code?: string }): Promise<JoinResponse> {
  const res = await fetch(`${BASE_URL}/api/sessions/join`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify(payload),
  });

  const ct = res.headers.get('content-type') ?? '';
  const body: unknown = ct.includes('application/json')
    ? await res.json()
    : await res.text();

  if (!res.ok) {
    const message =
      typeof body === 'object' && body !== null && 'message' in body
        ? String((body as { message: unknown }).message)
        : `HTTP ${res.status}`;
    throw new ApiError(res.status, message, body);
  }

  return body as JoinResponse;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/**
 * Parse QR code data as a URL to extract sessionId + token.
 * Doctor's ActiveSessionPage encodes: {origin}/join?sessionId={id}&token={qrToken}
 */
function parseQrUrl(data: string): { sessionId: string; token: string } | null {
  try {
    const url = new URL(data);
    const sessionId = url.searchParams.get('sessionId');
    const token = url.searchParams.get('token');
    if (sessionId && token) return { sessionId, token };
  } catch {
    // Not a URL
  }
  return null;
}

function getErrorMessage(err: unknown): string {
  if (err instanceof ApiError) {
    if (err.status === 404 || err.status === 410) return STRINGS.errorExpired;
    if (err.status === 400 || err.status === 422) return STRINGS.errorInvalid;
    return STRINGS.errorGeneric;
  }
  return STRINGS.errorGeneric;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function JoinPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // sessionId from URL (set when patient navigates via doctor's join link)
  const urlSessionId = searchParams.get('sessionId');
  const urlToken = searchParams.get('token');

  // Manual code entry state
  const [code, setCode] = useState('');
  const [isJoining, setIsJoining] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // QR scanning state
  const [scanMode, setScanMode] = useState(false);
  const [cameraError, setCameraError] = useState(false);

  // Refs for camera / canvas
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const rafRef = useRef<number | null>(null);
  const scanningRef = useRef(false);

  // -------------------------------------------------------------------------
  // Navigation after successful join
  // -------------------------------------------------------------------------

  const onJoinSuccess = useCallback(
    (sessionId: string, wsToken: string) => {
      // Store the patient JWT for PatientSessionPage to use when connecting its WebSocket
      sessionStorage.setItem(SESSION_TOKEN_KEY, wsToken);
      void navigate(`/session/${sessionId}/disclaimer`);
    },
    [navigate],
  );

  // -------------------------------------------------------------------------
  // Submit join request — two modes:
  //   1. Token mode (QR/URL): { sessionId, token } — 64-char hex
  //   2. Code mode (manual):  { sessionId, code }  — 6-digit numeric
  // -------------------------------------------------------------------------

  const submitWithToken = useCallback(
    async (sessionId: string, token: string) => {
      if (isJoining) return;
      setIsJoining(true);
      setError(null);

      try {
        const result = await joinSession({ sessionId, token: token.trim() });
        onJoinSuccess(result.sessionId, result.wsToken);
      } catch (err) {
        setError(getErrorMessage(err));
        setIsJoining(false);
      }
    },
    [isJoining, onJoinSuccess],
  );

  const submitWithCode = useCallback(
    async (sessionId: string | null, code: string) => {
      if (isJoining) return;
      setIsJoining(true);
      setError(null);

      try {
        const payload: { sessionId?: string; code: string } = { code: code.trim() };
        if (sessionId) payload.sessionId = sessionId;
        const result = await joinSession(payload);
        onJoinSuccess(result.sessionId, result.wsToken);
      } catch (err) {
        setError(getErrorMessage(err));
        setIsJoining(false);
      }
    },
    [isJoining, onJoinSuccess],
  );

  /** Called when QR scanner decodes data — parse URL to extract sessionId + token */
  const handleQrScan = useCallback(
    (qrData: string) => {
      const parsed = parseQrUrl(qrData);
      if (parsed) {
        void submitWithToken(parsed.sessionId, parsed.token);
      } else {
        setError(STRINGS.errorInvalid);
      }
    },
    [submitWithToken],
  );

  // -------------------------------------------------------------------------
  // QR scanning — camera setup + frame loop
  // -------------------------------------------------------------------------

  const stopCamera = useCallback(() => {
    if (rafRef.current !== null) {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    }
    scanningRef.current = false;
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }
  }, []);

  const startCamera = useCallback(async () => {
    setCameraError(false);
    setError(null);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'environment' },
        audio: false,
      });
      streamRef.current = stream;

      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        await videoRef.current.play();
      }

      scanningRef.current = true;

      const scan = () => {
        if (!scanningRef.current) return;
        const video = videoRef.current;
        const canvas = canvasRef.current;
        if (video && canvas && video.readyState === HTMLMediaElement.HAVE_ENOUGH_DATA) {
          const ctx = canvas.getContext('2d');
          if (ctx) {
            canvas.width = video.videoWidth;
            canvas.height = video.videoHeight;
            ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
            const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
            const qrCode = jsQR(imageData.data, imageData.width, imageData.height, {
              inversionAttempts: 'dontInvert',
            });
            if (qrCode?.data) {
              // Found a QR code — stop scanning and submit
              scanningRef.current = false;
              stopCamera();
              setScanMode(false);
              handleQrScan(qrCode.data);
              return;
            }
          }
        }
        rafRef.current = requestAnimationFrame(scan);
      };

      rafRef.current = requestAnimationFrame(scan);
    } catch {
      setCameraError(true);
      setScanMode(false);
      setError(STRINGS.errorCamera);
    }
  }, [stopCamera, handleQrScan]);

  // Toggle scan mode — only update state; the useEffect below drives camera start
  const handleToggleScan = useCallback(() => {
    if (scanMode) {
      stopCamera();
      setScanMode(false);
    } else {
      setScanMode(true);
    }
  }, [scanMode, stopCamera]);

  // Auto-join when URL contains both sessionId and token (direct link from doctor)
  const autoJoinAttempted = useRef(false);
  useEffect(() => {
    if (urlSessionId && urlToken && !autoJoinAttempted.current) {
      autoJoinAttempted.current = true;
      void submitWithToken(urlSessionId, urlToken);
    }
  }, [urlSessionId, urlToken, submitWithToken]);

  // Clean up camera on unmount
  useEffect(() => {
    return () => {
      stopCamera();
    };
  }, [stopCamera]);

  // Start camera when scan mode activates (single call site)
  useEffect(() => {
    if (scanMode) {
      void startCamera();
    }
    // startCamera is stable (useCallback with stable deps); safe to omit from array
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scanMode]);

  // -------------------------------------------------------------------------
  // Manual code entry handlers
  // -------------------------------------------------------------------------

  const handleCodeChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value.replace(/\D/g, '').slice(0, 6);
      setCode(value);
      setError(null);
    },
    [],
  );

  const handleManualSubmit = useCallback(
    (e: React.FormEvent) => {
      e.preventDefault();
      if (code.length === 6) {
        void submitWithCode(urlSessionId, code);
      }
    },
    [code, urlSessionId, submitWithCode],
  );

  // -------------------------------------------------------------------------
  // Render
  // -------------------------------------------------------------------------

  return (
    <div
      dir="rtl"
      lang="ar"
      className="flex flex-col items-center gap-8 w-full"
      style={{ fontFamily: "'Source Sans 3', sans-serif" }}
    >
      {/* Title */}
      <div className="text-center">
        <h1
          className="font-heading font-semibold text-clinical-charcoal"
          style={{ fontSize: '28px', lineHeight: '1.3' }}
        >
          {STRINGS.title}
        </h1>
        <p
          className="text-clinical-text-muted mt-2"
          style={{ fontSize: '18px' }}
        >
          {STRINGS.subtitle}
        </p>
      </div>

      {/* Error message */}
      {error && (
        <div
          role="alert"
          className="w-full rounded-clinical border border-red-200 bg-red-50 px-5 py-4 text-center text-red-700"
          style={{ fontSize: '18px' }}
        >
          {error}
        </div>
      )}

      {/* QR Scanner section */}
      <div className="w-full flex flex-col items-center gap-4">
        {scanMode && !cameraError && (
          <div className="relative w-full rounded-clinical overflow-hidden bg-black aspect-square max-w-xs">
            <video
              ref={videoRef}
              className="w-full h-full object-cover"
              playsInline
              muted
              aria-label={STRINGS.scanPrompt}
            />
            {/* Invisible canvas for frame analysis */}
            <canvas ref={canvasRef} className="hidden" aria-hidden="true" />

            {/* Viewfinder overlay */}
            <div
              className="absolute inset-0 flex items-center justify-center"
              aria-hidden="true"
            >
              <div
                className="border-2 border-white rounded-lg opacity-60"
                style={{ width: '65%', height: '65%' }}
              />
            </div>

            {/* Scanning label */}
            <p
              className="absolute bottom-3 w-full text-center text-white"
              style={{ fontSize: '16px' }}
            >
              {STRINGS.scanning}
            </p>
          </div>
        )}

        {/* Scan / Cancel button */}
        <button
          type="button"
          onClick={handleToggleScan}
          disabled={isJoining}
          className="w-full flex items-center justify-center gap-3 rounded-clinical border-2 border-clinical-teal text-clinical-teal font-body font-semibold transition-colors hover:bg-clinical-teal/10 disabled:opacity-50 disabled:cursor-not-allowed"
          style={{
            minHeight: '56px',
            fontSize: '20px',
          }}
        >
          {/* Camera icon */}
          <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth={2}
            strokeLinecap="round"
            strokeLinejoin="round"
            width={24}
            height={24}
            aria-hidden="true"
          >
            <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z" />
            <circle cx="12" cy="13" r="4" />
          </svg>
          {scanMode ? STRINGS.cancelScan : STRINGS.scanButton}
        </button>
      </div>

      {/* Divider */}
      <div className="flex items-center w-full gap-4">
        <div className="flex-1 h-px bg-clinical-border" />
        <span
          className="text-clinical-text-muted font-body"
          style={{ fontSize: '18px' }}
        >
          {STRINGS.orDivider}
        </span>
        <div className="flex-1 h-px bg-clinical-border" />
      </div>

      {/* Manual code entry */}
      <form
        onSubmit={handleManualSubmit}
        className="w-full flex flex-col items-center gap-6"
        noValidate
        aria-label={STRINGS.manualLabel}
      >
        <label
          htmlFor="session-code"
          className="text-clinical-charcoal font-body font-semibold text-center w-full"
          style={{ fontSize: '20px' }}
        >
          {STRINGS.manualLabel}
        </label>

        {/* 6-digit ATM-style input */}
        <input
          id="session-code"
          type="tel"
          inputMode="numeric"
          pattern="[0-9]{6}"
          maxLength={6}
          value={code}
          onChange={handleCodeChange}
          placeholder={STRINGS.manualPlaceholder}
          disabled={isJoining}
          autoComplete="one-time-code"
          className="w-full text-center tracking-[0.5em] text-clinical-charcoal bg-white border-2 border-clinical-border rounded-clinical focus:outline-none focus:border-clinical-teal focus:ring-2 focus:ring-clinical-teal/30 transition-colors disabled:opacity-50"
          style={{
            fontSize: '36px',
            minHeight: '72px',
            letterSpacing: '0.5em',
            fontFamily: 'monospace',
            direction: 'ltr',
          }}
          aria-describedby={error ? 'join-error' : undefined}
        />

        {/* Join button */}
        <button
          type="submit"
          disabled={code.length !== 6 || isJoining}
          className="w-full rounded-clinical bg-clinical-charcoal text-white font-body font-semibold transition-all hover:bg-clinical-charcoal-soft disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          style={{
            minHeight: '56px',
            fontSize: '20px',
          }}
        >
          {isJoining ? STRINGS.joining : STRINGS.joinButton}
        </button>
      </form>
    </div>
  );
}
